import React, { useState, useEffect } from 'react';
import { PipelineStage, pipelineAPI } from '../../services/api';
import { BoltIcon } from '@heroicons/react/24/outline';
import { Modal, Button, Card, CardBody } from '../ui';

interface StageModalProps {
  pipelineId: number;
  stage: PipelineStage | null;
  onClose: () => void;
}

const PRESET_COLORS = [
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Green', value: '#10B981' },
  { name: 'Emerald', value: '#059669' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Red', value: '#DC2626' },
  { name: 'Gray', value: '#6B7280' },
];

const LIFECYCLE_STAGES = [
  { value: '', label: 'None (No mapping)' },
  { value: 'SUBSCRIBER', label: 'Subscriber' },
  { value: 'LEAD', label: 'Lead' },
  { value: 'MQL', label: 'MQL' },
  { value: 'SQL', label: 'SQL' },
  { value: 'OPPORTUNITY', label: 'Opportunity' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'EVANGELIST', label: 'Evangelist' },
  { value: 'OTHER', label: 'Other' },
];

const LEAD_STATUSES = [
  { value: '', label: 'None (No mapping)' },
  { value: 'NEW', label: 'New' },
  { value: 'ATTEMPTING_TO_CONTACT', label: 'Attempting to Contact' },
  { value: 'CONNECTED', label: 'Connected' },
  { value: 'OPEN_DEAL', label: 'Open Deal' },
  { value: 'UNQUALIFIED', label: 'Unqualified' },
  { value: 'BAD_TIMING', label: 'Bad Timing' },
  { value: 'LOST', label: 'Lost' },
];

const StageModal: React.FC<StageModalProps> = ({ pipelineId, stage, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order: 0,
    defaultProbability: 50,
    isClosedWon: false,
    isClosedLost: false,
    color: '#3B82F6',
    mappedLifecycleStage: '',
    mappedLeadStatus: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (stage) {
      setFormData({
        name: stage.name,
        description: stage.description || '',
        order: stage.order,
        defaultProbability: stage.defaultProbability,
        isClosedWon: stage.isClosedWon,
        isClosedLost: stage.isClosedLost,
        color: stage.color || '#3B82F6',
        mappedLifecycleStage: stage.mappedLifecycleStage || '',
        mappedLeadStatus: stage.mappedLeadStatus || '',
      });
    }
  }, [stage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prepare data - convert empty strings to undefined for optional fields
      const submitData: any = {
        name: formData.name,
        description: formData.description,
        order: formData.order,
        defaultProbability: formData.defaultProbability,
        isClosedWon: formData.isClosedWon,
        isClosedLost: formData.isClosedLost,
        color: formData.color,
      };

      // Only include mappings if they have values
      if (formData.mappedLifecycleStage) {
        submitData.mappedLifecycleStage = formData.mappedLifecycleStage;
      }
      if (formData.mappedLeadStatus) {
        submitData.mappedLeadStatus = formData.mappedLeadStatus;
      }

      if (stage) {
        await pipelineAPI.updateStage(stage.id, submitData);
      } else {
        await pipelineAPI.createStage(pipelineId, submitData);
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save stage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={stage ? 'Edit Stage' : 'Create Stage'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Card variant="bordered" className="border-error-200 bg-error-50">
            <CardBody className="p-3">
              <p className="text-sm text-error-600">{error}</p>
            </CardBody>
          </Card>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-neutral-700">
              Stage Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
              placeholder="e.g., Qualified"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-neutral-700">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
              placeholder="Describe this stage"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">
              Display Order
            </label>
            <input
              type="number"
              min="0"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">
              Default Probability (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.defaultProbability}
              onChange={(e) => setFormData({ ...formData, defaultProbability: parseInt(e.target.value) })}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Color
            </label>
            <div className="grid grid-cols-9 gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: color.value })}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    formData.color === color.value ? 'border-neutral-900 scale-110' : 'border-neutral-300 hover:scale-105'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-6 pt-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isClosedWon"
              checked={formData.isClosedWon}
              onChange={(e) => setFormData({
                ...formData,
                isClosedWon: e.target.checked,
                isClosedLost: e.target.checked ? false : formData.isClosedLost
              })}
              className="h-4 w-4 text-success-600 focus:ring-success-500 border-neutral-300 rounded"
            />
            <label htmlFor="isClosedWon" className="ml-2 block text-sm text-neutral-900">
              Closed Won
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isClosedLost"
              checked={formData.isClosedLost}
              onChange={(e) => setFormData({
                ...formData,
                isClosedLost: e.target.checked,
                isClosedWon: e.target.checked ? false : formData.isClosedWon
              })}
              className="h-4 w-4 text-error-600 focus:ring-error-500 border-neutral-300 rounded"
            />
            <label htmlFor="isClosedLost" className="ml-2 block text-sm text-neutral-900">
              Closed Lost
            </label>
          </div>
        </div>

        {/* Client Lifecycle Mapping Section */}
        <div className="border-t border-neutral-200 pt-4 mt-4">
          <h4 className="text-sm font-medium text-neutral-900 mb-3 flex items-center">
            <BoltIcon className="h-4 w-4 mr-2 text-primary-500" />
            Auto-Update Client on Stage Change
          </h4>
          <p className="text-xs text-neutral-500 mb-3">
            When a client is moved to this stage, automatically update their lifecycle stage and lead status.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700">
                Set Lifecycle Stage
              </label>
              <select
                value={formData.mappedLifecycleStage}
                onChange={(e) => setFormData({ ...formData, mappedLifecycleStage: e.target.value })}
                className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
              >
                {LIFECYCLE_STAGES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700">
                Set Lead Status
              </label>
              <select
                value={formData.mappedLeadStatus}
                onChange={(e) => setFormData({ ...formData, mappedLeadStatus: e.target.value })}
                className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
              >
                {LEAD_STATUSES.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={loading}>
            {stage ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default StageModal;

